"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLoading,
  IonToast,
  useIonViewWillEnter,
} from "@ionic/react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Container,
  Stack,
  Autocomplete,
} from "@mui/material"
import {
  Add,
  Delete,
  ExpandMore,
  Inventory,
  ShoppingCart,
  Cancel,
  CheckCircle,
  Pending,
  Warning,
  Refresh,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import { getBarInventory, getBarInventoryByBranch, getBarInventoryByBranch_empty, getStewardInventory } from "../../../api/products/product"
import {
  addBarRequest,
  addStewardRequest,
  deleteBarRequestOrder,
  deleteRequestOrder,
  getMyBarRequestsWithItems,
  getMyBarRequestsWithItems_branch,
  getStewardBarRequestsWithItems,
} from "../../../api/stock-transfer/stock-transfer"

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
  },
}))

const HeaderCard = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  padding: theme.spacing(3),
  borderRadius: "12px 12px 0 0",
}))

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: "center",
  borderRadius: 8,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
}))

interface RequestItem {
  id: string
  product_id: string
  product_name: string
  quantity: number
  status?: string
}

interface Product {
  id: string
  product_id: string
  name: string
  qty: number
}

interface UserRequest {
  request_id: string
  status: string
  created_at: string
  items: Array<{
    name: string
    quantity: number
    quantity_approved?: number
    status: string
  }>
}

const BranchRequisitionPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([])
  const [requestItems, setRequestItems] = useState<RequestItem[]>([])
  const [currentProduct, setCurrentProduct] = useState("")
  const [currentQuantity, setCurrentQuantity] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [userRequests, setUserRequests] = useState<UserRequest[]>([])
  const [stockCount, setStockCount] = useState(0)
  const [totalQuantity, setTotalQuantity] = useState(0)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)

  useEffect(() => {
    fetchProducts()
    fetchUserRequests()
  }, [])

  useIonViewWillEnter(() => {
    fetchProducts()
    fetchUserRequests()
  })

  const fetchProducts = async () => {
    setLoading(true)
    try {
      const response = await getBarInventoryByBranch_empty()
      setProducts(response.data)
      setStockCount(response.totalStockCount)
    } catch (error) {
      console.log(error)
      setToastMessage("Failed to fetch products")
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const deleteRequest = async (request_id: string) => {
    const data = {
      request_id: request_id,
      //   requestID: requestID(),
    };
    setLoading(true)
    try {
     const response = await deleteBarRequestOrder(data)
     console.log(response)
      setToastMessage("Request cancelled successfully")
      setShowToast(true)
      fetchUserRequests()
    } catch (error) {
      console.log(error)
      setToastMessage("Failed to cancel request")
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserRequests_kip = async () => {
    try {
      const response = await getMyBarRequestsWithItems()
      setUserRequests(response.data.data)
    } catch (error) {
      setToastMessage("Failed to fetch your requests")
      setShowToast(true)
    }
  }
 const fetchUserRequests = async () => {
    try {
      const response = await getMyBarRequestsWithItems_branch()
      console.log("User Requests Response:", response)
      setUserRequests(response.data.data)
    } catch (error) {
      setToastMessage("Failed to fetch your requests")
      setShowToast(true)
    }
  }
  const addRequestItem = () => {
    if (!currentProduct || !currentQuantity) return

    const selectedProduct = products.find((p) => p.product_id === currentProduct)
    if (!selectedProduct) return

    const newItem: RequestItem = {
      id: Date.now().toString(),
      product_id: currentProduct,
      product_name: selectedProduct.name,
      quantity: currentQuantity,
    }

    setRequestItems([...requestItems, newItem])
    setCurrentProduct("")
    setCurrentQuantity(0)
  }

  const removeRequestItem = (id: string) => {
    setRequestItems(requestItems.filter((item) => item.id !== id))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (requestItems.length === 0) {
      setToastMessage("Please add at least one item to request")
      setShowToast(true)
      return
    }

    setLoading(true)
    try {
      await addBarRequest(requestItems)
      setToastMessage("Request submitted successfully!")
      setRequestItems([])
      fetchUserRequests()
    } catch (error) {
      setToastMessage("Error submitting request")
    } finally {
      setLoading(false)
      setShowToast(true)
    }
  }

  useEffect(() => {
    const total = requestItems.reduce((acc, item) => acc + Number(item.quantity || 0), 0)
    setTotalQuantity(total)
  }, [requestItems])

  const handleCancel = async (requestId: string) => {
    setUserRequests(userRequests.filter((request) => request.request_id !== requestId))
    deleteRequest(requestId)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle sx={{ color: "#10b981", fontSize: 20 }} />
      case "rejected":
        return <Cancel sx={{ color: "#ef4444", fontSize: 20 }} />
      case "pending":
        return <Pending sx={{ color: "#f59e0b", fontSize: 20 }} />
      default:
        return <Warning sx={{ color: "#6b7280", fontSize: 20 }} />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "success"
      case "rejected":
        return "error"
      case "pending":
        return "warning"
      default:
        return "default"
    }
  }

  return (
    <IonPage className="pushDown">
      <IonHeader className="ion-no-border ">
        <IonToolbar>
          <IonTitle> Stock Request</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <Container maxWidth="lg" sx={{ py: 2 }}>
          {/* Stats Overview */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4}>
              <StatsCard>
                <Inventory sx={{ fontSize: 28, mb: 1, color: "#64748b" }} />
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  {stockCount}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  Current Stock
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatsCard>
                <ShoppingCart sx={{ fontSize: 28, mb: 1, color: "#64748b" }} />
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  {userRequests?.length}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  Total Requests
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatsCard>
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  {totalQuantity}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  Items in Cart
                </Typography>
              </StatsCard>
            </Grid>
          </Grid>

          {/* Create New Request */}
          <StyledCard sx={{ mb: 3 }}>
            <HeaderCard>
              <Typography
                variant="h6"
                fontWeight="600"
                sx={{ display: "flex", alignItems: "center", gap: 1, color: "#1e293b" }}
              >
                <Add sx={{ color: "#64748b" }} />
                Create New Request
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                Add products to your stock request
              </Typography>
            </HeaderCard>
            <CardContent sx={{ p: 3 }}>
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={5}>
  <Autocomplete
  fullWidth
  value={products?.find((p) => p.product_id === currentProduct) || null}
  onChange={(event, newValue) => setCurrentProduct(newValue?.product_id || "")}
  options={products || []}
  getOptionLabel={(option) => option.name || ""}
  renderOption={(props, option) => (
    <Box
      component="li"
      {...props}
      key={option.product_id}   // ✅ ensures uniqueness
      sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
    >
      <span>{option.name}</span>
    </Box>
  )}
  renderInput={(params) => (
    <TextField {...params} label="Select Product" variant="outlined" />
  )}
/>


                   {/* <Autocomplete
  options={products || []}
  getOptionLabel={(option) => option.name || ""}
  renderOption={(props, option, index) => (
    <Box
      component="li"
      {...props}
      key={`${option.product_id}-${index}`}   // ✅ unique key
      sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
    >
      <span>{option.name}</span>
      <Chip
        size="small"
        label={`${option.qty} available`}
        sx={{
          bgcolor: "#f1f5f9",
          color: "#475569",
          fontSize: "0.75rem",
        }}
      />
    </Box>
  )}
  renderInput={(params) => (
    <TextField {...params} label="Select Product" variant="outlined" />
  )}
/> */}

                    {/* <FormControl fullWidth>
                      <InputLabel>Select Product</InputLabel>
                      <Select
                        value={currentProduct}
                        label="Select Product"
                        onChange={(e) => {setCurrentProduct(e.target.value),

                          console.log(currentProduct)
                        }



                        }
                      >
                        {products?.map((product) => (
                          <MenuItem key={product.id} value={product.product_id}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                              <span>{product.name}</span>
                              <Chip
                                size="small"
                                label={`${product.qty} available`}
                                sx={{
                                  bgcolor: "#f1f5f9",
                                  color: "#475569",
                                  fontSize: "0.75rem",
                                }}
                              />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>  */}
 
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="number"
                      value={currentQuantity || ""}
                      onChange={(e) => {setCurrentQuantity(Number(e.target.value) || 0)
                       

}}               
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Add />}
                      onClick={addRequestItem}
                      disabled={!currentProduct || !currentQuantity}
                      sx={{
                        py: 1.5,
                        bgcolor: "#1e293b",
                        "&:hover": { bgcolor: "#334155" },
                      }}
                    >
                      Add Item
                    </Button>
                  </Grid>
                </Grid>

                {/* Request Items Table */}
                {requestItems.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: "#1e293b", fontWeight: 600 }}>
                      Request Items ({requestItems.length})
                    </Typography>
                    <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: "#f8fafc" }}>
                            <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Product</TableCell>
                            <TableCell align="right" sx={{ fontWeight: "600", color: "#374151" }}>
                              Quantity
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: "600", color: "#374151" }}>
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {requestItems.map((item) => (
                            <TableRow key={item.id} hover>
                              <TableCell sx={{ color: "#374151" }}>{item.product_name}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={item.quantity}
                                  size="small"
                                  sx={{ bgcolor: "#f1f5f9", color: "#475569" }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => removeRequestItem(item.id)}
                                  sx={{ color: "#ef4444" }}
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: "#f8fafc" }}>
                            <TableCell sx={{ fontWeight: "600", color: "#1e293b" }}>Total Items</TableCell>
                            <TableCell align="right" sx={{ fontWeight: "600", color: "#1e293b" }}>
                              {totalQuantity}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    mt: 3,
                    py: 1.5,
                    bgcolor: "#1e293b",
                    "&:hover": { bgcolor: "#334155" },
                  }}
                  disabled={loading || requestItems.length === 0}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Submit Request"}
                </Button>
              </Box>
            </CardContent>
          </StyledCard>

          {/* My Requests */}
          <StyledCard>
            <HeaderCard>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography variant="h6" fontWeight="600" color="#1e293b">
                    My Requests
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                    Track your submitted requests
                  </Typography>
                </Box>
                <IconButton onClick={fetchUserRequests} sx={{ color: "#64748b" }}>
                  <Refresh />
                </IconButton>
              </Box>
            </HeaderCard>
            <CardContent sx={{ p: 0 }}>
              {userRequests?.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <ShoppingCart sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
                  <Typography variant="h6" color="#64748b">
                    No requests found
                  </Typography>
                  <Typography variant="body2" color="#94a3b8">
                    Create your first stock request above
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={0}>
                  {userRequests?.map((request) => (
                    <Accordion key={request.request_id} sx={{ "&:before": { display: "none" }, boxShadow: "none" }}>
                      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, borderBottom: "1px solid #f1f5f9" }}>
                        <Box
                          sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {getStatusIcon(request.status)}
                            <Box>
                              <Typography fontWeight="600" color="#1e293b">
                                Request #{request.request_id}
                              </Typography>
                              <Typography variant="caption" color="#64748b">
                                {new Date(request.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {request.status !== "approved" && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={request.status !== "pending"}
                                sx={{
                                  color: "#ef4444",
                                  borderColor: "#ef4444",
                                  "&:hover": { bgcolor: "#fef2f2", borderColor: "#ef4444" },
                                }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSelectedRequestId(request.request_id)
                                  setConfirmOpen(true)
                                }}
                              >
                                Cancel
                              </Button>
                            )}
                            <Chip
                              label={request.status}
                              size="small"
                              color={getStatusColor(request.status) as any}
                              sx={{ textTransform: "capitalize" }}
                            />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pt: 0 }}>
                        <TableContainer component={Paper} sx={{ borderRadius: 1, border: "1px solid #e2e8f0" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Product</TableCell>
                                <TableCell align="right" sx={{ fontWeight: "600", color: "#374151" }}>
                                  Requested
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: "600", color: "#374151" }}>
                                  Approved
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: "600", color: "#374151" }}>
                                  Status
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {request?.items?.map((item: any, i) => (
                                <TableRow key={i} hover>
                                  <TableCell sx={{ color: "#374151" }}>{item.name}</TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label={item.quantity}
                                      size="small"
                                      sx={{ bgcolor: "#f1f5f9", color: "#475569" }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label={item.quantity_approved || "-"}
                                      size="small"
                                      sx={{
                                        bgcolor: item.quantity_approved ? "#dcfce7" : "#f1f5f9",
                                        color: item.quantity_approved ? "#166534" : "#475569",
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={item.status}
                                      size="small"
                                      color={getStatusColor(item.status) as any}
                                      sx={{ textTransform: "capitalize" }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              )}
            </CardContent>
          </StyledCard>
        </Container>

        {/* Confirmation Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Warning sx={{ color: "#f59e0b" }} />
            Confirm Cancellation
          </DialogTitle>
          <DialogContent>
            <Typography color="#374151">
              Are you sure you want to cancel this request? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              variant="outlined"
              sx={{ color: "#64748b", borderColor: "#d1d5db" }}
            >
              Keep Request
            </Button>
            <Button
              onClick={() => {
                if (selectedRequestId) {
                  handleCancel(selectedRequestId)
                }
                setConfirmOpen(false)
              }}
              variant="contained"
              sx={{ bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" } }}
            >
              Yes, Cancel Request
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading isOpen={loading} message="Processing..." />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="top"
        />
      </IonContent>
    </IonPage>
  )
}

export default BranchRequisitionPage
